import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { recruitmentCompanyVacancyService } from '../../services/recruitmentCompanyVacancyService';
import { toast } from 'react-toastify';
import RecruitmentCompanyNav from '../../components/RecruitmentCompanyNav';
import RecruitmentCompanyBottomNavigation from '../../components/RecruitmentCompanyBottomNavigation';
import { authService } from '../../services/authService';
import Loader from '../../components/Loader';
import Swal from 'sweetalert2';
import { useTranslation } from '../../hooks/useTranslation';

const RecruitmentCompanyVacancyManagement = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const { t, locale } = useTranslation();
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVacancies();
  }, []);

  const loadVacancies = async () => {
    try {
      setLoading(true);
      const response = await recruitmentCompanyVacancyService.getAll();

      // Handle different response formats
      if (response && response.success) {
        setVacancies(response.data || []);
      } else if (Array.isArray(response)) {
        // If response is directly an array
        setVacancies(response);
      } else if (response?.data) {
        // If data is nested
        setVacancies(Array.isArray(response.data) ? response.data : []);
      } else if (response?.message) {
        // API returned an error message
        console.error('API Error:', response.message);
        toast.error(response.message || t('company.vacancyManagement.loadFailed'));
        setVacancies([]);
      } else {
        console.warn('Unexpected response format:', response);
        setVacancies([]);
      }
    } catch (error) {
      console.error('Error loading vacancies:', error);
      const errorMessage = error.response?.data?.message || error.message || t('company.vacancyManagement.loadFailed');
      console.error('Error details:', error.response?.data || error.message);
      toast.error(errorMessage);
      setVacancies([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('company.vacancyManagement.notSpecified');
    const date = new Date(dateString);
    if (locale === 'ar') {
      const months = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    } else {
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    }
  };

  const formatStatus = (status) => {
    if (status === 'open') return t('company.vacancyManagement.open');
    if (status === 'closed') return t('company.vacancyManagement.closed');
    return status;
  };

  const getStatusBadgeClass = (status) => {
    return status === 'open' ? 'status-badge-accepted' : 'status-badge-rejected';
  };

  const handleDelete = async (id) => {
    const dir = locale === 'ar' ? 'rtl' : 'ltr';
    const result = await Swal.fire({
      html: `<div style="text-align: center; direction: ${dir};">
        <p>${t('company.vacancyManagement.deleteConfirm')}</p>
        <p style="color: #dc2626; font-size: 0.85rem; margin-top: 0.5rem;">${t('company.vacancyManagement.deleteWarning')}</p>
      </div>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg> ${t('company.vacancyManagement.delete')}</span>`,
      cancelButtonText: `<span style="display: flex; align-items: center; gap: 0.5rem;"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> ${t('company.settings.cancel')}</span>`,
      reverseButtons: locale === 'ar',
      customClass: {
        popup: locale === 'ar' ? 'swal2-rtl' : 'swal2-popup-modern',
        title: locale === 'ar' ? 'swal2-title-rtl' : 'swal2-title-modern',
        htmlContainer: locale === 'ar' ? 'swal2-html-container-rtl' : 'swal2-html-container-modern',
        confirmButton: locale === 'ar' ? 'swal2-confirm-delete-rtl' : 'swal2-confirm-modern',
        cancelButton: locale === 'ar' ? 'swal2-cancel-rtl' : 'swal2-cancel-modern'
      }
    });

    if (result.isConfirmed) {
      try {
        const response = await recruitmentCompanyVacancyService.delete(id);
        if (response.success) {
          toast.success(t('company.vacancyManagement.deleteSuccess'));
          loadVacancies();
        }
      } catch (error) {
        toast.error(t('company.vacancyManagement.deleteFailed'));
      }
    }
  };

  // Get company name for display (either for_company or recruitment company)
  const getCompanyDisplayName = (vacancy) => {
    if (vacancy.for_company_name) {
      return vacancy.for_company_name;
    }
    if (vacancy.for_company?.name) {
      return vacancy.for_company.name;
    }
    if (vacancy.recruitment_company?.name) {
      return vacancy.recruitment_company.name;
    }
    return t('employee.home.companyName');
  };

  if (loading) {
    return <Loader fullScreen={true} size="large" />;
  }

  return (
    <div className="employee-home-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <RecruitmentCompanyNav user={user} />

      {/* Header Section */}
      <div className="px-3 mb-4 mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h3 className="section-title mb-1">{t('company.vacancyManagement.title')}</h3>
            <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>{t('company.vacancyManagement.subtitle') || 'Manage all published vacancies'}</p>
          </div>
          <Link
            to="/recruitment-company/vacancies/create"
            className="btn-apply"
            style={{
              background: 'linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%)',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.75rem',
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}
          >
            + {t('company.vacancyManagement.createNew')}
          </Link>
        </div>
      </div>

      {/* Vacancies List */}
      <div className="all-jobs-section px-3 mb-5">
        {vacancies.length === 0 ? (
          <div className="text-center py-5">
            <p className="empty-state-text mb-3">{t('company.vacancyManagement.noVacancies')}</p>
            <Link
              to="/recruitment-company/vacancies/create"
              className="btn-apply"
              style={{
                background: 'linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%)',
                color: 'white',
                padding: '0.875rem 2rem',
                borderRadius: '0.75rem',
                textDecoration: 'none',
                fontSize: '1rem',
                fontWeight: '600',
                display: 'inline-block'
              }}
            >
              {t('company.vacancyManagement.createNew')}
            </Link>
          </div>
        ) : (
          <div className="jobs-list">
            {vacancies.map(vacancy => (
              <div key={vacancy.id} className="job-card mb-3">
                <div className="d-flex align-items-start gap-3">
                  <div className="company-logo-card">
                    {vacancy.for_company?.logo || vacancy.recruitment_company?.logo ? (
                      <img
                        src={vacancy.for_company?.logo || vacancy.recruitment_company?.logo}
                        alt="Company Logo"
                        className="logo-img"
                      />
                    ) : (
                      <div className="company-logo-text text-dark align-content-center">
                        <b>
                          {getCompanyDisplayName(vacancy)?.charAt(0).toUpperCase() || 'C'}
                        </b>
                      </div>
                    )}
                  </div>
                  <div className="job-content flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="flex-grow-1">
                        <div className="job-title-card mb-1">
                          {vacancy.job_title?.name || t('employee.home.jobTitle')}
                        </div>
                        <div className="job-company-location mb-2">
                          {getCompanyDisplayName(vacancy)}
                        </div>
                        <div className="job-tags d-flex gap-2 mb-2">
                          <span className={`status-badge ${getStatusBadgeClass(vacancy.status)}`}>
                            {formatStatus(vacancy.status)}
                          </span>
                          {vacancy.created_at && (
                            <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                              {formatDate(vacancy.created_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      <Link
                        to={`/recruitment-company/vacancies/${vacancy.id}`}
                        className="btn-view-vacancy"
                      >
                        {t('company.vacancyManagement.viewDetails')}
                      </Link>
                      <Link
                        to={`/recruitment-company/vacancies/${vacancy.id}/applications`}
                        className="btn-view-vacancy"
                      >
                        {t('company.vacancyManagement.viewApplications')}
                      </Link>
                      <button
                        onClick={() => handleDelete(vacancy.id)}
                        className="btn-withdraw"
                      >
                        {t('company.vacancyManagement.delete')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <RecruitmentCompanyBottomNavigation />
    </div>
  );
};

export default RecruitmentCompanyVacancyManagement;

